// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, TextInput, StatusBanner, Button, useToast } from '@loa/ui-components';
import { agencyCodeApi } from '../api/agencyCodeApi';

/** The minimal provider shape the modal needs — satisfied by both the list
 *  row DTO and the detail page's provider + summary blocks. */
export interface ProviderSettingsTarget {
  id: string;
  name: string;
  advisorAgencyCode?: string;
  brokerageAgencyCode?: string;
}

type ProviderSettingsModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  /** Called after a successful save/remove so the caller can refresh the list. */
  onSaved: () => void;
  provider: ProviderSettingsTarget;
  advisorId: string;
}>;

/**
 * Per-provider settings editable by the advisor.
 *
 * Today that is the advisor's personal agency-code override (the same
 * surface as Settings → Agency Codes, scoped to one provider). The
 * brokerage default is shown read-only — it is managed by the brokerage
 * admin in the Admin UI.
 */
export function ProviderSettingsModal({
  open,
  onClose,
  onSaved,
  provider,
  advisorId,
}: ProviderSettingsModalProps) {
  const { showToast } = useToast();
  const hasOverride = Boolean(provider.advisorAgencyCode);
  const [code, setCode] = useState(provider.advisorAgencyCode ?? '');
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the input whenever the modal opens for a (possibly different) provider
  useEffect(() => {
    if (open) {
      setCode(provider.advisorAgencyCode ?? '');
      setError(null);
    }
  }, [open, provider.id, provider.advisorAgencyCode]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const extractMessage = (err: unknown): string | undefined => {
    if (err && typeof err === 'object' && 'response' in err) {
      return (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    }
    return undefined;
  };

  const handleSave = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Agency code is required. Use "Remove personal code" to revert to the brokerage default.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      if (hasOverride) {
        await agencyCodeApi.updateAgencyCode(advisorId, provider.id, trimmed);
      } else {
        await agencyCodeApi.createAgencyCode(advisorId, provider.id, trimmed);
      }
      showToast('Agency code saved successfully.', 'success');
      onSaved();
      handleClose();
    } catch (err) {
      setError(extractMessage(err) || 'Failed to save agency code');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setRemoving(true);
      setError(null);
      await agencyCodeApi.deleteAgencyCode(advisorId, provider.id);
      showToast('Personal agency code removed.', 'success');
      onSaved();
      handleClose();
    } catch (err) {
      setError(extractMessage(err) || 'Failed to remove agency code');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <Modal.Header
        icon="settings gear"
        title={`${provider.name} settings`}
        onClose={handleClose}
      />
      <Modal.Content>
        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}

        <div className="flex flex-col gap-xl">
          <TextInput
            label="Your agency code"
            placeholder="Enter your agency code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={50}
            autoFocus
            fluid
          />

          <p className="m-0 font-sans text-body-03 text-grey-02">
            {provider.brokerageAgencyCode
              ? `Brokerage default: ${provider.brokerageAgencyCode}. Your personal code takes precedence when set.`
              : 'No brokerage default is configured for this provider. Contact your brokerage admin to set one.'}
          </p>

          {hasOverride && (
            <div>
              <Button
                variant="outlined"
                onClick={() => { void handleRemove(); }}
                disabled={loading || removing}
                data-testid="remove-agency-code"
              >
                Remove personal code
              </Button>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: loading || removing }}
        primaryAction={{
          label: 'Save Changes',
          onClick: () => { void handleSave(); },
          loading,
          disabled: loading || removing,
        }}
      />
    </Modal>
  );
}
